import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { authApi } from '../services/authApi';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    const refreshSession = useCallback(async () => {
        if (process.env.NODE_ENV === 'test') {
            setIsLoading(false);
            return;
        }

        try {
            const data = await authApi.me();
            setUser(data.user || null);
        } catch (error) {
            setUser(null);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        refreshSession();
    }, [refreshSession]);

    const register = async (payload) => {
        const data = await authApi.register(payload);
        setUser(data.user || null);
        return data;
    };

    const login = async (payload) => {
        const data = await authApi.login(payload);
        setUser(data.user || null);
        return data;
    };

    const logout = async () => {
        try {
            await authApi.logout();
        } finally {
            setUser(null);
        }
    };

    const value = useMemo(() => ({
        user,
        isLoading,
        isAuthenticated: Boolean(user),
        refreshSession,
        register,
        login,
        logout,
    }), [user, isLoading, refreshSession]);

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
}
